import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { StoreProfile, BusinessHours } from "@/types/store";
import { getCachedGeocode } from "@/utils/geocoding";
import { loadGoogleMapsApi } from "@/utils/googleMaps";
import { getFormattedBusinessHours, formatTime } from "@/utils/businessHours";
import { useCallback, useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useToast } from "@/components/ui/use-toast";
import { redirect } from "react-router-dom";

// Default business hours for new stores
const defaultBusinessHours: Record<string, BusinessHours> = {
  monday: { open: "09:00", close: "17:00", closed: false },
  tuesday: { open: "09:00", close: "17:00", closed: false },
  wednesday: { open: "09:00", close: "17:00", closed: false },
  thursday: { open: "09:00", close: "17:00", closed: false },
  friday: { open: "09:00", close: "17:00", closed: false },
  saturday: { open: "09:00", close: "17:00", closed: false },
  sunday: { open: "09:00", close: "17:00", closed: true },
};

export interface UpdateStoreProfileInput {
  store_name: string;
  description?: string | null;
  phone?: string | null;
  email?: string | null;
  storefront_image_url?: string | null;
  cover_image_url?: string | null;
  use_storefront_as_cover?: boolean;
  business_hours: Record<string, BusinessHours>;
  unit_number?: string | null;
  street_number?: string | null;
  street_name?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string;
  delivery_radius: number;
  delivery_fee?: number | null;
  minimum_order_amount?: number | null;
  distance_type: 'radius' | 'driving' | 'polygon' | 'suburb';
  use_multiple_zones: boolean;
  delivery_zones: Array<{ radius: number; fee: number }>;
  delivery_zone_polygon?: number[][];
  delivery_suburbs?: Array<{ suburb: string; postcode: string; state: string; }>;
  same_day_enabled: boolean;
  delivery_days: string[];
  delivery_cutoff_times: Record<string, string>;
  non_sameday_enabled: boolean;
  non_sameday_days: string[];
  /**
   * Time slots for non-same-day delivery
   * 
   * IMPORTANT: This field name MUST match the database column name 'non_sameday_time_slots'
   * Do not create fields with similar names (like non_sameday_slots) to avoid mismatch issues.
   * 
   * Each time slot requires:
   * - label: A human-readable name for the time slot (e.g., "Morning", "Afternoon")
   * - start_time: Time in format "HH:MM" using 24-hour time
   * - end_time: Time in format "HH:MM" using 24-hour time 
   */
  non_sameday_time_slots: Array<{ label: string; start_time: string; end_time: string; }>;
  blackout_dates: string[];
}

interface UseStoreProfileProps {
  floristId: string;
}

export function useStoreProfile() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [storeProfile, setStoreProfile] = useState<StoreProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const getStoreProfile = async () => {
      if (!user?.id) return;
      
      try {
        const { data: profile, error: profileError } = await supabase
          .from('store_profiles')
          .select('*')
          .eq('florist_id', user.id)
          .maybeSingle();
          
        if (profileError) {
          console.error('Failed to get store profile:', profileError);
          setError('Could not load store profile. Please try again later.');
          return;
        }
        
        if (profile) {
          setStoreProfile(profile);
        } else {
          setError('Could not find store profile. Please complete your store setup.');
        }
      } catch (error) {
        console.error('Failed to get store profile:', error);
        setError('Could not load store profile. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    getStoreProfile();
  }, [user]);

  return {
    storeProfile,
    loading,
    error
  };
}

export function useStoreProfileById({ floristId }: UseStoreProfileProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const {
    data: storeProfile,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["store-profile", floristId],
    queryFn: async () => {
      if (!floristId) {
        throw new Error("Florist ID is required");
      }

      if (!user) {
        throw new Error("User must be authenticated");
      }

      console.log("Fetching store profile for florist:", floristId);
      console.log("Current user:", user);

      const { data, error } = await supabase
        .from("store_profiles")
        .select("*")
        .eq("florist_id", floristId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching store profile:", error);
        throw error;
      }

      return data as StoreProfile;
    },
    enabled: !!floristId && !!user,
    refetchOnWindowFocus: false,
  });

  const updateProfile = useMutation({
    mutationFn: async (input: UpdateStoreProfileInput) => {
      if (!floristId) {
        throw new Error("Florist ID is required");
      }

      if (!user) {
        throw new Error("User must be authenticated");
      }

      console.log("Starting profile update for florist:", floristId);
      console.log("Current user:", user);

      // First get the current store profile
      const { data: currentProfile, error: fetchError } = await supabase
        .from("store_profiles")
        .select("*")
        .eq("florist_id", floristId)
        .single();

      if (fetchError) {
        console.error("Error fetching current profile:", fetchError);
        throw fetchError;
      }

      if (!currentProfile) {
        throw new Error("Store profile not found");
      }

      console.log("Current profile:", currentProfile);

      // Get coordinates for the address if address fields are provided
      let coordinates = currentProfile.coordinates;
      let geocodedAddressData = currentProfile.geocoded_address;
      
      // Check if any address field has changed
      const addressChanged = 
        input.unit_number !== currentProfile.unit_number ||
        input.street_number !== currentProfile.street_number ||
        input.street_name !== currentProfile.street_name ||
        input.suburb !== currentProfile.suburb ||
        input.state !== currentProfile.state ||
        input.postcode !== currentProfile.postcode ||
        input.country !== currentProfile.country;
      
      console.log("Address changed check:", {
        changed: addressChanged,
        old_suburb: currentProfile.suburb,
        new_suburb: input.suburb,
        old_state: currentProfile.state,
        new_state: input.state
      });

      // Force re-geocoding if address changed
      if (addressChanged && input.street_number && input.street_name && input.suburb && input.state) {
        try {
          console.log("Re-geocoding address due to address change...");
          
          // Initialize geocoder if needed
          await loadGoogleMapsApi();
          
          // Check if Google Maps API is loaded
          if (typeof window.google === 'undefined' || !window.google.maps || !window.google.maps.Geocoder) {
            console.error("Google Maps Geocoder not available");
          } else {
            const geocoder = new window.google.maps.Geocoder();
            
            // Build address string for geocoding
            const addressParts = [
              input.unit_number,
              input.street_number,
              input.street_name,
              input.suburb,
              input.state,
              input.country || "Australia"
            ].filter(Boolean);
            
            const address = addressParts.join(' ');
            console.log("Geocoding new address:", address);
            
            // Get geocode result
            const result = await geocoder.geocode({ address });
            
            if (result.results.length > 0) {
              const location = result.results[0].geometry.location;
              coordinates = {
                lat: location.lat(),
                lng: location.lng()
              };
              
              // Save the complete geocode result including formatted address and components
              geocodedAddressData = JSON.stringify({
                formatted_address: result.results[0].formatted_address,
                address_components: result.results[0].address_components,
                place_id: result.results[0].place_id,
                geometry: result.results[0].geometry,
                types: result.results[0].types,
                // Also store extracted location data for easier access
                suburb: input.suburb,
                state: input.state
              });
              
              console.log("New geocode result:", {
                coordinates,
                formatted_address: result.results[0].formatted_address,
                suburb: input.suburb,
                state: input.state
              });
            } else {
              console.warn("No geocoding results found for address:", address);
            }
          }
        } catch (error) {
          console.error("Error geocoding address:", error);
          // Continue with update even if geocoding fails
        }
      } else if (input.street_number && input.street_name && input.suburb && input.state) {
        try {
          console.log("Geocoding address...");
          
          // Initialize geocoder if needed
          await loadGoogleMapsApi();
          
          // Check if Google Maps API is loaded
          if (typeof window.google === 'undefined' || !window.google.maps || !window.google.maps.Geocoder) {
            console.error("Google Maps Geocoder not available");
          } else {
            const geocoder = new window.google.maps.Geocoder();
            
            // Build address string for geocoding
            const addressParts = [
              input.unit_number,
              input.street_number,
              input.street_name,
              input.suburb,
              input.state,
              input.country || "Australia"
            ].filter(Boolean);
            
            const address = addressParts.join(' ');
            
            // Get full geocode result to update both coordinates and geocoded_address
            const result = await geocoder.geocode({ address });
            
            if (result.results.length > 0) {
              const location = result.results[0].geometry.location;
              coordinates = {
                lat: location.lat(),
                lng: location.lng()
              };
              
              // Save the complete geocode result including formatted address and components
              geocodedAddressData = JSON.stringify({
                formatted_address: result.results[0].formatted_address,
                address_components: result.results[0].address_components,
                place_id: result.results[0].place_id,
                geometry: result.results[0].geometry,
                types: result.results[0].types,
                // Also store extracted location data for easier access
                suburb: input.suburb,
                state: input.state
              });
              
              console.log("Full geocode result saved:", {
                coordinates,
                formatted_address: result.results[0].formatted_address,
                data_to_save: {
                  suburb: input.suburb,
                  state: input.state,
                  address_components: result.results[0].address_components
                }
              });
            } else {
              console.warn("No geocoding results found for address:", address);
            }
          }
        } catch (error) {
          console.error("Error geocoding address:", error);
          // Continue with update even if geocoding fails
        }
      }

      // Clean and prepare the input data while preserving existing fields
      const cleanedInput = {
        ...currentProfile,
        store_name: input.store_name.trim(),
        description: input.description?.trim() || null,
        phone: input.phone?.trim() || null,
        email: input.email?.trim() || null,
        storefront_image_url: input.storefront_image_url || null,
        cover_image_url: input.cover_image_url || null,
        use_storefront_as_cover: input.use_storefront_as_cover ?? false,
        business_hours: input.business_hours ? Object.keys(input.business_hours).reduce((acc, day) => {
          acc[day] = {
            open: input.business_hours[day]?.open || defaultBusinessHours[day].open,
            close: input.business_hours[day]?.close || defaultBusinessHours[day].close,
            closed: input.business_hours[day]?.closed !== undefined ? input.business_hours[day].closed : defaultBusinessHours[day].closed
          };
          return acc;
        }, {} as Record<string, BusinessHours>) : defaultBusinessHours,
        unit_number: input.unit_number?.trim() || null,
        street_number: input.street_number?.trim() || null,
        street_name: input.street_name?.trim() || null,
        suburb: input.suburb?.trim() || null,
        state: input.state?.trim() || null,
        postcode: input.postcode?.trim() || null,
        country: input.country?.trim() || "Australia",
        coordinates: coordinates, // Use updated coordinates if geocoding was successful
        geocoded_address: geocodedAddressData, // Include the full geocoded address data
        delivery_radius: input.delivery_radius,
        delivery_fee: input.delivery_fee !== undefined ? input.delivery_fee : null,
        minimum_order_amount: input.minimum_order_amount !== undefined ? input.minimum_order_amount : null,
        distance_type: input.distance_type || "radius",
        use_multiple_zones: input.use_multiple_zones ?? false,
        delivery_zones: input.delivery_zones || [],
        same_day_enabled: input.same_day_enabled ?? false,
        delivery_days: input.delivery_days || [],
        delivery_cutoff_times: input.delivery_cutoff_times || {},
        non_sameday_enabled: input.non_sameday_enabled ?? true,
        non_sameday_time_slots: input.non_sameday_time_slots || [],
        non_sameday_days: input.non_sameday_days || [],
        blackout_dates: input.blackout_dates || [],
        updated_at: new Date().toISOString()
      };

      // Special handling for blackout_dates to ensure it's a proper array
      console.log("Original blackout_dates:", input.blackout_dates);
      console.log("Type of blackout_dates:", typeof input.blackout_dates);
      console.log("Is Array?", Array.isArray(input.blackout_dates));
      
      // Database constraint: valid_blackout_dates CHECK (blackout_dates IS NULL OR blackout_dates != '{}')
      // We need to always have at least one date in the array
      if (!Array.isArray(input.blackout_dates) || input.blackout_dates.length === 0) {
        console.warn("blackout_dates is empty or not an array, adding placeholder date");
        // If attempting to set empty array, add a placeholder far-future date
        cleanedInput.blackout_dates = ["2099-12-31"]; // Use a date far in the future
      } else {
        // Ensure all blackout_dates are strings and there's at least one
        const validDates = input.blackout_dates
          .filter(date => date) // Filter out null/undefined
          .map(date => String(date)); // Convert to string
        
        cleanedInput.blackout_dates = validDates.length > 0 ? validDates : ["2099-12-31"];
      }
      
      console.log("Cleaned blackout_dates:", cleanedInput.blackout_dates);

      // Remove fields that shouldn't be updated
      delete cleanedInput.id;
      delete cleanedInput.florist_id;
      delete cleanedInput.created_at;

      console.log("Saving to database with business_hours:", cleanedInput.business_hours);
      
      // Create a new object just for the update to avoid any unexpected fields
      const updateData = {
        store_name: cleanedInput.store_name,
        description: cleanedInput.description,
        phone: cleanedInput.phone,
        email: cleanedInput.email,
        storefront_image_url: cleanedInput.storefront_image_url,
        cover_image_url: cleanedInput.cover_image_url,
        use_storefront_as_cover: cleanedInput.use_storefront_as_cover,
        business_hours: cleanedInput.business_hours,
        unit_number: cleanedInput.unit_number,
        street_number: cleanedInput.street_number,
        street_name: cleanedInput.street_name,
        suburb: cleanedInput.suburb,
        state: cleanedInput.state,
        postcode: cleanedInput.postcode,
        country: cleanedInput.country,
        coordinates: cleanedInput.coordinates,
        geocoded_address: cleanedInput.geocoded_address,
        delivery_radius: cleanedInput.delivery_radius,
        delivery_fee: cleanedInput.delivery_fee,
        minimum_order_amount: cleanedInput.minimum_order_amount,
        distance_type: cleanedInput.distance_type,
        use_multiple_zones: cleanedInput.use_multiple_zones,
        delivery_zones: cleanedInput.delivery_zones,
        same_day_enabled: cleanedInput.same_day_enabled,
        delivery_days: cleanedInput.delivery_days,
        delivery_cutoff_times: cleanedInput.delivery_cutoff_times,
        non_sameday_enabled: cleanedInput.non_sameday_enabled,
        non_sameday_time_slots: cleanedInput.non_sameday_time_slots,
        non_sameday_days: cleanedInput.non_sameday_days,
        blackout_dates: cleanedInput.blackout_dates,
        updated_at: cleanedInput.updated_at
      };
      
      console.log("Update object blackout_dates:", updateData.blackout_dates);
      
      const { data: updatedData, error: updateError } = await supabase
        .from("store_profiles")
        .update(updateData)
        .eq("florist_id", floristId)
        .select()
        .single();

      if (updateError) {
        console.error("Error updating profile:", updateError);
        throw updateError;
      }

      console.log("Successfully updated profile:", updatedData);
      console.log("Updated business_hours:", updatedData.business_hours);
      console.log("Updated blackout_dates:", updatedData.blackout_dates);
      return updatedData as StoreProfile;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["store-profile", floristId] });
      toast.success("Store profile updated successfully");
    },
    onError: (error) => {
      console.error("Store profile update failed:", error);
      toast.error(error instanceof Error ? error.message : "Failed to update store profile");
    },
  });

  const activateStore = useMutation({
    mutationFn: async () => {
      if (!floristId) {
        throw new Error("Florist ID is required");
      }

      const { data: store, error: fetchError } = await supabase
        .from("store_profiles")
        .select("*")
        .eq("florist_id", floristId)
        .single();

      if (fetchError) {
        console.error("Error fetching store:", fetchError);
        throw fetchError;
      }

      if (!store) {
        throw new Error("Store not found");
      }

      // Check if we need to geocode the address
      let coordinates = store.coordinates;
      let geocodedAddressData = store.geocoded_address;
      
      if (!coordinates && store.street_number && store.street_name && store.suburb && store.state) {
        try {
          console.log("Geocoding address for store activation...");
          
          // Initialize geocoder if needed
          await loadGoogleMapsApi();
          
          // Check if Google Maps API is loaded
          if (typeof window.google === 'undefined' || !window.google.maps || !window.google.maps.Geocoder) {
            console.error("Google Maps Geocoder not available");
          } else {
            const geocoder = new window.google.maps.Geocoder();
            
            // Build address string for geocoding
            const addressParts = [
              store.unit_number,
              store.street_number,
              store.street_name,
              store.suburb,
              store.state,
              store.country || "Australia"
            ].filter(Boolean);
            
            const address = addressParts.join(' ');
            
            // Get full geocode result
            const result = await geocoder.geocode({ address });
            
            if (result.results.length > 0) {
              const location = result.results[0].geometry.location;
              coordinates = {
                lat: location.lat(),
                lng: location.lng()
              };
              
              // Save the complete geocode result including formatted address and components
              geocodedAddressData = JSON.stringify({
                formatted_address: result.results[0].formatted_address,
                address_components: result.results[0].address_components,
                place_id: result.results[0].place_id,
                geometry: result.results[0].geometry,
                types: result.results[0].types,
                // Also store extracted location data for easier access
                suburb: store.suburb,
                state: store.state
              });
              
              console.log("Full geocode result saved for activation:", {
                coordinates,
                formatted_address: result.results[0].formatted_address
              });
            } else {
              console.warn("No geocoding results found for address:", address);
            }
          }
        } catch (error) {
          console.error("Error geocoding address during activation:", error);
          // Continue with activation even if geocoding fails
        }
      }

      const { error: updateError } = await supabase
        .from("store_profiles")
        .update({
          store_status: "active",
          approval_status: "approved",
          coordinates: coordinates, // Include updated coordinates
          geocoded_address: geocodedAddressData, // Include full geocoded address data
          updated_at: new Date().toISOString()
        })
        .eq("id", store.id);

      if (updateError) {
        console.error("Error updating store:", updateError);
        throw updateError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store-profile", floristId] });
      toast.success("Store activated successfully");
    },
    onError: (error) => {
      console.error("Error activating store:", error);
      toast.error("Failed to activate store");
    }
  });

  const isStoreOpen = (): boolean => {
    if (!storeProfile?.business_hours) return false;

    const now = new Date();
    const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const day = days[now.getDay()];
    const hours = storeProfile.business_hours[day];

    if (!hours || hours.closed) return false;

    const currentTime = now.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    });

    return currentTime >= hours.open && currentTime <= hours.close;
  };

  const getNextOpenTime = (): string | null => {
    if (!storeProfile?.business_hours) return null;

    const now = new Date();
    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    let currentDayIndex = now.getDay();
    let daysChecked = 0;

    while (daysChecked < 7) {
      const day = days[currentDayIndex];
      const hours = storeProfile.business_hours[day];

      if (!hours.closed) {
        const currentTime = now.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        });

        if (daysChecked === 0 && currentTime <= hours.close) {
          if (currentTime < hours.open) {
            return `Today at ${formatTime(hours.open)}`;
          }
        } else {
          return `${capitalizeFirstLetter(day)} at ${formatTime(hours.open)}`;
        }
      }

      currentDayIndex = (currentDayIndex + 1) % 7;
      daysChecked++;
    }

    return null;
  };

  const capitalizeFirstLetter = (string: string): string => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return {
    storeProfile,
    isLoading,
    error,
    updateStoreProfile: updateProfile.mutateAsync,
    isStoreOpen,
    getNextOpenTime,
    activateStore: activateStore.mutateAsync,
    getFormattedBusinessHours,
    isLoadingActivation: activateStore.isPending,
    formatTime
  };
} 